import React, {useMemo, useCallback, useState} from 'react';
import {Sidebar, SidebarBody, SidebarHead} from './Sidebar';
import LazyAsyncList from '../LazyAsyncList';
import fetchPaginatedJSON from '../../../utils/fetchPaginatedJSON';
import theme from '../../../theme';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import CircularProgress from '@material-ui/core/CircularProgress';
import combineAsyncIterables from '../../../utils/combineAsyncIterables';
import {TrackDispatcher} from '../../../hooks/useTracks';
import {WaypointDispatcher} from '../../../hooks/useWaypoints';
import {
    trackFixedLayerIds,
    waypointFixedLayerIds
} from '../../../utils/gaia/getSyncMapboxStyle/fixedLayerIds';
import fetchTrackMultiLine from '../../../utils/gaia/fetchTrackMultiLine';
import convertLngLat from '../../../utils/mapbox/convertLngLat';
import getBounds from '@turf/bbox';
import mapboxgl from 'mapbox-gl';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import {sortBy} from 'lodash';

type ObjectSummary = (
    TrackSummary |
    WaypointSummary |
    Waypoint['properties'] & {path: 'waypoint'} |
    Track['properties'] & {path: 'track'}
);

export interface DataSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    onSidebarChange: (sidebar: MapSidebar) => void;
    onPopupChange: SetState<MapPopup>;
    popup: MapPopup;
    trackDispatcher: TrackDispatcher;
    waypointDispatcher: WaypointDispatcher;
    onOverlaysChange: SetState<MapOverlays>;
    overlays: MapOverlays;
    map: mapboxgl.Map | undefined;
    project: MapProject;
    interaction: MapInteraction;
    waypoints: Record<string, Waypoint>;
    tracks: Record<string, Track>;
}

// eslint-disable-next-line prefer-arrow-callback
export default React.memo(function DataSidebar({
    onSidebarChange,
    onPopupChange,
    popup,
    trackDispatcher,
    waypointDispatcher,
    onOverlaysChange,
    overlays,
    map,
    project,
    interaction,
    waypoints,
    tracks,
    ...props
}: DataSidebarProps) {

    const iterable = useMemo(() => {
        return combineAsyncIterables<ObjectSummary>(
            object => Date.parse(object.time_created!),
            'desc',
            // eslint-disable-next-line max-len
            fetchPaginatedJSON<WaypointSummary>('/api/objects/waypoint/?sort_field=create_date&sort_direction=desc'),
            // eslint-disable-next-line max-len
            fetchPaginatedJSON<TrackSummary>('/api/objects/track/?sort_field=create_date&sort_direction=desc'),
            // eslint-disable-next-line max-len
            fetchPaginatedJSON<TrackSummary>('/api/objects/route/?sort_field=create_date&sort_direction=desc'),
        );
    }, []);

    const localWaypoints: ObjectSummary[] = Object.values(waypoints)
        .filter(waypoint => (
            waypoint.properties.isLocallyCreated &&
            !waypoint.properties.trackLocalId
        ))
        .map(waypoint => ({...waypoint.properties, path: 'waypoint'}));

    const localTracks: ObjectSummary[] = Object.values(tracks)
        .filter(track => track.properties.isLocallyCreated)
        .map(track => ({...track.properties, path: 'track'}));

    const localObjects = sortBy([...localWaypoints, ...localTracks], object =>
        object.time_created && -Date.parse(object.time_created)
    );

    return <Sidebar {...props}>
        <SidebarHead onSidebarChange={onSidebarChange} title="Data" />
        <SidebarBody
            style={{
                overflowY: 'hidden',
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(1),
            }}
        >
            <List disablePadding>
                {localObjects.map(object =>
                    <DataSidebarListItem
                        key={object.id}
                        object={object}
                        onPopupChange={onPopupChange}
                        popup={popup}
                        trackDispatcher={trackDispatcher}
                        waypointDispatcher={waypointDispatcher}
                        onOverlaysChange={onOverlaysChange}
                        overlays={overlays}
                        map={map}
                        project={project}
                        interaction={interaction}
                        waypoints={waypoints}
                        tracks={tracks}
                    />
                )}
            </List>
            <LazyAsyncList<ObjectSummary> iterable={iterable} disablePadding>
                {object =>
                    <DataSidebarListItem
                        key={object.id}
                        object={object}
                        onPopupChange={onPopupChange}
                        popup={popup}
                        trackDispatcher={trackDispatcher}
                        waypointDispatcher={waypointDispatcher}
                        onOverlaysChange={onOverlaysChange}
                        overlays={overlays}
                        map={map}
                        project={project}
                        interaction={interaction}
                        waypoints={waypoints}
                        tracks={tracks}
                    />
                }
            </LazyAsyncList>
        </SidebarBody>
    </Sidebar>;
});

interface DataSidebarListItemProps {
    object: ObjectSummary;
    onPopupChange: SetState<MapPopup>;
    popup: MapPopup;
    trackDispatcher: TrackDispatcher;
    waypointDispatcher: WaypointDispatcher;
    onOverlaysChange: SetState<MapOverlays>;
    overlays: MapOverlays;
    map: mapboxgl.Map | undefined;
    project: MapProject;
    interaction: MapInteraction;
    waypoints: Record<string, Waypoint>;
    tracks: Record<string, Track>;
}

function DataSidebarListItem({
    onPopupChange,
    popup,
    waypointDispatcher,
    trackDispatcher,
    object: remoteObject,
    onOverlaysChange,
    overlays,
    map,
    project,
    interaction,
    waypoints,
    tracks,
}: DataSidebarListItemProps) {

    const [isLoading, setIsLoading] = useState(false);

    const object = remoteObject.path === 'waypoint' ?
        {...remoteObject, ...remoteObject.id && waypoints[remoteObject.id]?.properties} :
        {...remoteObject, ...remoteObject.id && tracks[remoteObject.id]?.properties};

    const onClick = useCallback(() => {
        setIsLoading(true);
        if (object.path === 'waypoint') {
            onOverlaysChange({
                ...overlays,
                showWaypoints: true,
            });
            waypointDispatcher({
                type: 'takeLocal',
                waypoint: {properties: object},
                callback: (waypoint: Waypoint) => {
                    if (!map) return;
                    map.flyTo({
                        center: convertLngLat(waypoint.geometry.coordinates),
                        zoom: 16,
                        duration: 750
                    });
                    setIsLoading(false);
                }
            });
            onPopupChange({
                type: 'point',
                pointLocalId: (object.id || object.pointLocalId)!,
                mapboxLayerId: waypointFixedLayerIds.localUser.pin,
            });
        } else {
            onOverlaysChange({
                ...overlays,
                showTracks: true,
            });
            trackDispatcher({
                type: 'takeLocal',
                track: {properties: {...object, isPublicTrack: false}},
                callback: async (track: Track) => {
                    if (!map) return;
                    const multiline = await fetchTrackMultiLine({
                        project,
                        track,
                        interaction,
                    });
                    const bounds = new mapboxgl.LngLatBounds(getBounds(multiline) as any);
                    const popupPoint = nearestPointOnLine(
                        multiline.geometry,
                        bounds.getCenter().toArray()
                    );

                    map.fitBounds(bounds, {padding: 20, duration: 750});

                    onPopupChange({
                        type: 'track',
                        trackLocalId: (object.id || object.trackLocalId)!,
                        mapboxLayerId: trackFixedLayerIds.localUser.normal,
                        lngLat: convertLngLat(popupPoint!.geometry!.coordinates),
                    });

                    setIsLoading(false);
                }
            });
        }
    // eslint-disable-next-line max-len
    }, [interaction, map, object, onOverlaysChange, onPopupChange, overlays, project, trackDispatcher, waypointDispatcher]);

    if (object.isLocallyDeleted) return null;

    return <ListItem
        button
        onClick={onClick}
        selected={Boolean(popup && (
            (popup.type === 'point' && object.id === popup.pointLocalId) ||
            (popup.type === 'track' && object.id === popup.trackLocalId)
        ))}
    >
        <ListItemText
            primary={object.title}
            secondary={object.time_created ?
                (new Date(object.time_created)).toLocaleString() :
                '...'
            }
        />
        <ListItemSecondaryAction>
            {isLoading && <CircularProgress disableShrink thickness={7} />}
        </ListItemSecondaryAction>
    </ListItem>;
}
